/**
 * RettVerk 1.2.1 - App.js (Optimalisert)
 * Hovedapplikasjon med lazy-loading og kode-splitting
 * 
 * Optimaliseringer:
 * - React.lazy for tunge sider (Document, Timeline, Profile, Privacy)
 * - Suspense med loading-spinner
 * - Forhåndslasting av biblioteker ved dokumentmodul-navigasjon
 * - Landing page som root for uautentiserte brukere
 * 
 * LANDING PAGE TOGGLE:
 * Sett ENABLE_LANDING_PAGE til false for å deaktivere landing page
 */

// ============================================================
// LANDING PAGE KONFIGURASJON
// Sett til false for å gå direkte til /auth
// ============================================================
const ENABLE_LANDING_PAGE = true;

import React, { useEffect, useState, Suspense, lazy } from "react";
import "@/index.css";
import "@/styles/mobilePreview.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { VaultSecurityProvider } from "./context/VaultSecurityContext";
import { checkOnboardingStatus, ONBOARDING_STATUS } from "./services/onboardingService";

// Eager-loaded pages (kritiske for initial render)
import AuthPage from "./pages/AuthPage";
import ChatPage from "./pages/ChatPage";
import OnboardingPage from "./pages/OnboardingPage";
// UpdatePopup removed

// Landing page (eager-loaded for rask førstesidevisning)
import { LandingPage } from "./modules/landing";

// Company portal components (B2B kunde-portal)
import { 
  CompanyLayout, 
  RequireCompanyUser, 
  RequireCompanyAdmin 
} from "./company";

// Lazy-loaded pages (ikke kritiske for initial render)
const DocumentPage = lazy(() => import("./pages/DocumentPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const VilkarPage = lazy(() => import("./pages/VilkarPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const EncryptionSetupPage = lazy(() => import("./pages/EncryptionSetupPage"));

// B2C Private user pages
const PurchasePage = lazy(() => import("./pages/private/PurchasePage"));

// B2B landing page
const BedriftPage = lazy(() => import("./modules/landing/pages/BedriftPage"));
const KontaktSalgPage = lazy(() => import("./modules/landing/pages/KontaktSalgPage"));
const BedriftAuthPage = lazy(() => import("./pages/BedriftAuthPage"));
// B2B Dashboard (legacy - redirects to /company)
const B2BDashboard = lazy(() => import("./pages/B2BDashboard"));
// B2B Admin Dashboard
const AdminPage = lazy(() => import("./pages/AdminPage"));

// System Admin Dashboard (Eier-tilgang)
const SystemAdminDashboard = lazy(() => import("./admin/pages/SystemAdminDashboard"));

// Company portal pages (lazy-loaded)
const CompanyDashboard = lazy(() => import("./company/pages/CompanyDashboard"));
const CompanyDocuments = lazy(() => import("./company/pages/CompanyDocuments"));
const CompanyModules = lazy(() => import("./company/pages/CompanyModules"));
const CompanyUsers = lazy(() => import("./company/pages/CompanyUsers"));
const CompanySettings = lazy(() => import("./company/pages/CompanySettings"));
const CompanyModuleView = lazy(() => import("./company/pages/CompanyModuleView"));
const CompanyBilling = lazy(() => import("./company/pages/BillingPage"));
const BarnehageDashboard = lazy(() => import("./company/pages/BarnehageDashboard"));

// Arbeidsrett modules (separate for Privat and Bedrift)
const ArbeidsrettPrivatPage = lazy(() => import("./modules/arbeidsrett-privat/pages/ArbeidsrettPrivatPage"));
const ArbeidsrettBedriftPage = lazy(() => import("./modules/arbeidsrett-bedrift/pages/ArbeidsrettBedriftPage"));
const ArbeidsrettBedriftGuide = lazy(() => import("./modules/arbeidsrett-bedrift/pages/ArbeidsrettBedriftGuide"));

// Rettigheter og krav - Specialized pages
const LonnskravPage = lazy(() => import("./modules/arbeidsrett-privat/pages/lonnskrav/LonnskravPage"));
const FeriepengerPage = lazy(() => import("./modules/arbeidsrett-privat/pages/feriepenger/FeriepengerPage"));
const ArbeidstidPage = lazy(() => import("./modules/arbeidsrett-privat/pages/arbeidstid/ArbeidstidPage"));

const TimelinePage = lazy(() => 
  import("./modules/timeline/TimelinePage").then(module => ({ 
    default: module.default 
  }))
);
const BarnevernPageV2 = lazy(() => 
  import("./modules/barnevern_v2/pages/BarnevernPageV2")
);
const ForbrukerrettPage = lazy(() =>
  import("./modules/forbrukerrett/pages/ForbrukerrettPage")
);
const ArbeidsrettPage = lazy(() =>
  import("./modules/arbeidsrett/pages/ArbeidsrettPage")
);
const HusleierettPage = lazy(() =>
  import("./modules/husleierett/pages/HusleierettPage")
);
const ForvaltningsrettPage = lazy(() =>
  import("./modules/forvaltningsrett/pages/ForvaltningsrettPage")
);
const EiendomsrettPage = lazy(() =>
  import("./modules/eiendomsrett/pages/EiendomsrettPage")
);
const ArverettPage = lazy(() =>
  import("./modules/arverett/pages/ArverettPage")
);
const TrygderettPage = lazy(() =>
  import("./modules/trygderett/pages/TrygderettPage")
);

// Layout
import PrivateLayout from "./components/layout/PrivateLayout";
const HusleierettGuide = lazy(() =>
  import("./modules/husleierett/pages/HusleierettGuide")
);
const ArbeidsrettGuide = lazy(() =>
  import("./modules/arbeidsrett/pages/ArbeidsrettGuide")
);
const ForbrukerrettGuide = lazy(() =>
  import("./modules/forbrukerrett/pages/ForbrukerrettGuide")
);
const BarnevernGuide = lazy(() =>
  import("./modules/barnevern_v2/pages/BarnevernGuide")
);

// Loading spinner komponent
const PageLoader = () => (
  <div className="min-h-screen bg-[var(--bg-deep)] flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-2 border-[var(--copper)]/30 border-t-[var(--copper)] rounded-full animate-spin" />
      <span className="text-[var(--text-secondary)] text-sm">Laster...</span>
    </div>
  </div>
);

// Mobile Preview Detection
function MobilePreviewManager() {
  useEffect(() => {
    const isPreview = window.location.hostname.includes('preview.emergentagent.com');
    
    if (isPreview) {
      document.body.classList.add('mobile-preview');
    } else {
      document.body.classList.remove('mobile-preview');
    }

    return () => {
      document.body.classList.remove('mobile-preview');
    };
  }, []);

  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      if (user) {
        const status = await checkOnboardingStatus();
        setOnboardingStatus(status);
      }
      setCheckingOnboarding(false);
    };
    checkStatus();
  }, [user]);

  if (loading || checkingOnboarding) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Skip onboarding check for system admin
  if (user.role === 'system_admin' || user.is_system_admin) {
    return children;
  }

  if (onboardingStatus?.needsOnboarding && 
      onboardingStatus?.status !== ONBOARDING_STATUS.STORAGE_UNAVAILABLE) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

// Landing Route - Viser landing page for uautentiserte, chat for autentiserte
function LandingRoute() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  // Autentisert bruker går til riktig dashboard basert på account_type og rolle
  if (user) {
    // System Admin går til admin dashboard
    if (user.role === 'system_admin' || user.is_system_admin) {
      return <Navigate to="/system-admin" replace />;
    }
    // B2B-brukere går til ny company portal
    return <Navigate to={user.account_type === 'business' ? '/company/dashboard' : '/chat'} replace />;
  }
  
  // Hvis landing page er deaktivert, gå til auth
  if (!ENABLE_LANDING_PAGE) {
    return <Navigate to="/auth" replace />;
  }
  
  // Uautentisert bruker ser landing page
  return <LandingPage />;
}

function AuthRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return <PageLoader />;
  }
  if (user) {
    // System Admin redirect
    if (user.role === 'system_admin' || user.is_system_admin) {
      return <Navigate to="/system-admin" replace />;
    }
    // Redirect to appropriate dashboard based on account type
    return <Navigate to={user.account_type === 'business' ? '/company/dashboard' : '/chat'} replace />;
  }
  return <AuthPage />;
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // System Admin should skip onboarding and go to admin dashboard
  if (user.role === 'system_admin' || user.is_system_admin) {
    return <Navigate to="/system-admin" replace />;
  }

  return <OnboardingPage />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <VaultSecurityProvider>
        <BrowserRouter>
          <MobilePreviewManager />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Landing page for uautentiserte (kan deaktiveres via ENABLE_LANDING_PAGE) */}
              <Route path="/" element={<LandingRoute />} />
              
              {/* B2B Landing */}
              <Route path="/bedrift" element={<BedriftPage />} />
              <Route path="/bedrift/kontakt-salg" element={<KontaktSalgPage />} />
              <Route path="/bedrift/auth" element={<BedriftAuthPage />} />
              
              {/* Auth */}
              <Route path="/auth" element={<AuthRoute />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/forgot-password" element={<ResetPasswordPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<AdminLoginPage />} />
              
              {/* System Admin Dashboard (Eier-tilgang) */}
              <Route path="/system-admin" element={
                <ProtectedRoute>
                  <SystemAdminDashboard />
                </ProtectedRoute>
              } />
              
              {/* Onboarding og setup */}
              <Route path="/onboarding" element={<OnboardingRoute />} />
              <Route path="/encryption-setup" element={<ProtectedRoute><EncryptionSetupPage /></ProtectedRoute>} />
              
              {/* ═══════════════════════════════════════════════════════════════ */}
              {/* B2B COMPANY PORTAL - Bedriftens eget dashboard */}
              {/* ═══════════════════════════════════════════════════════════════ */}
              <Route path="/company" element={
                <RequireCompanyUser>
                  <CompanyLayout />
                </RequireCompanyUser>
              }>
                {/* Dashboard - standardrute */}
                <Route index element={<Navigate to="/company/dashboard" replace />} />
                <Route path="dashboard" element={<CompanyDashboard />} />
                
                {/* Dokumenter */}
                <Route path="documents" element={<CompanyDocuments />} />
                
                {/* Moduloversikt */}
                <Route path="modules" element={<CompanyModules />} />
                
                {/* Modulvisning - rendres INNI CompanyLayout */}
                <Route path="modules/:moduleKey" element={<CompanyModuleView />} />
                
                {/* Brukere - KUN for company_admin */}
                <Route path="users" element={
                  <RequireCompanyAdmin>
                    <CompanyUsers />
                  </RequireCompanyAdmin>
                } />
                
                {/* Innstillinger */}
                <Route path="settings" element={<CompanySettings />} />
                
                {/* Fakturering - KUN for company_admin */}
                <Route path="billing" element={
                  <RequireCompanyAdmin>
                    <CompanyBilling />
                  </RequireCompanyAdmin>
                } />
                
                {/* B2B Guide for Arbeidsrett */}
                <Route path="arbeidsrett/guide" element={<ArbeidsrettBedriftGuide />} />
                
                {/* Modul-dashboards */}
                <Route path="dashboard/barnehage" element={<BarnehageDashboard />} />
              </Route>
              
              {/* Legacy B2B Dashboard - redirect til ny portal */}
              <Route path="/b2b" element={<Navigate to="/company/dashboard" replace />} />
              <Route path="/b2b/*" element={<Navigate to="/company/dashboard" replace />} />
              
              {/* B2B Admin Dashboard (for systemadministratorer) */}
              <Route path="/admin/*" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              
              {/* Hovedapplikasjon (privat) */}
              <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
              <Route path="/documents" element={<ProtectedRoute><DocumentPage /></ProtectedRoute>} />
              <Route path="/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
              <Route path="/barnevern" element={<ProtectedRoute><PrivateLayout><BarnevernPageV2 /></PrivateLayout></ProtectedRoute>} />
              <Route path="/forbrukerrett" element={<ProtectedRoute><PrivateLayout><ForbrukerrettPage /></PrivateLayout></ProtectedRoute>} />
              <Route path="/arbeidsrett" element={<ProtectedRoute><PrivateLayout><ArbeidsrettPage /></PrivateLayout></ProtectedRoute>} />
              <Route path="/arbeidsrett-privat" element={<ProtectedRoute><PrivateLayout><ArbeidsrettPrivatPage /></PrivateLayout></ProtectedRoute>} />
              <Route path="/husleierett" element={<ProtectedRoute><PrivateLayout><HusleierettPage /></PrivateLayout></ProtectedRoute>} />
              <Route path="/forvaltningsrett" element={<ProtectedRoute><PrivateLayout><ForvaltningsrettPage /></PrivateLayout></ProtectedRoute>} />
              <Route path="/eiendomsrett" element={<ProtectedRoute><PrivateLayout><EiendomsrettPage /></PrivateLayout></ProtectedRoute>} />
              <Route path="/arverett" element={<ProtectedRoute><PrivateLayout><ArverettPage /></PrivateLayout></ProtectedRoute>} />
              <Route path="/trygderett" element={<ProtectedRoute><PrivateLayout><TrygderettPage /></PrivateLayout></ProtectedRoute>} />
              <Route path="/husleierett/guide" element={<HusleierettGuide />} />
              <Route path="/arbeidsrett/guide" element={<ArbeidsrettGuide />} />
              <Route path="/forbrukerrett/guide" element={<ForbrukerrettGuide />} />
              <Route path="/barnevern/guide" element={<BarnevernGuide />} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              
              {/* Rettigheter og krav - Spesialiserte sider */}
              <Route path="/rettigheter/lonnskrav" element={<ProtectedRoute><LonnskravPage /></ProtectedRoute>} />
              <Route path="/rettigheter/feriepenger" element={<ProtectedRoute><FeriepengerPage /></ProtectedRoute>} />
              <Route path="/rettigheter/arbeidstid" element={<ProtectedRoute><ArbeidstidPage /></ProtectedRoute>} />
              
              {/* B2C Kjøp og konto */}
              <Route path="/konto/kjop" element={<ProtectedRoute><PurchasePage /></ProtectedRoute>} />
              
              {/* Offentlig */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/vilkar" element={<VilkarPage />} />
            </Routes>
          </Suspense>
          {/* UpdatePopup removed */}
        </BrowserRouter>
      </VaultSecurityProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
